import {Component, OnInit} from '@angular/core';
import { saveAs } from "file-saver";
import {Request} from "../../classes/request";
import {HttpErrorResponse} from "@angular/common/http";
import {ActivatedRoute, Router} from "@angular/router";
import {RequestService} from "../../services/request";
import {DocumentService} from "../../services/document";
import {Document} from "../../classes/document";
import {FileService} from "../../services/file";
import {AuthService} from "../../services/auth";
import {User} from "../../classes/user";
import {DepreciationAsset} from "../../interfaces/depreciationAsset";
import {
    hasMultipleDepreciationAssets,
    initializeDepreciationAssetsState
} from "../../utils/depreciationAssets";
import {NgbModal} from "@ng-bootstrap/ng-bootstrap";
import {RequestDeleteModalComponent} from "../../modals/request/request-delete-modal/request-delete-modal.component";
import {isCustomerDocumentType, isCustomerSatelliteDocument} from "../../utils/customerDocuments";
import {isDnsSabatiniViewRequest} from "../../utils/dnsProblems";
import {isIperViewRequest} from "../../utils/iperProblems";
import {openDocumentPreview} from "../../modals/document/document-preview-modal/document-preview-modal.component";

export type RequestDetailView = 'base' | 'dns' | 'iper';

@Component({
    selector: 'app-request-detail',
    templateUrl: './request-detail.component.html',
    styleUrls: ['./request-detail.component.css']
})
export class RequestDetailComponent implements OnInit {
    user: User = new User();
    request: Request = new Request();
    investmentAmountParsed: string = '';
    checkRequest4: boolean = false;
    activeView: RequestDetailView = 'base';
    documents: Array<Document> = [];
    documentTypes: any = {
        1: 'Visura camerale',
        2: 'Ultimo Bilancio approvato',
        3: 'Marca da bollo da 16 euro (foto o scanner)',
        4: 'Carta di identità del titolare della società',
        5: 'Offerta del fornitore non timbrata',
        6: 'Contratto + Privacy + Mandato professionale + Procura firmati digitalmente',
        7: 'Distinta di pagamento',
        8: 'DURC',
        9: 'Registri IVA',
        10: 'Fatture, DDT, Verbali di consegna',
        11: 'Perizia 4.0',
        12: 'Carta di identità del perito 4.0',
        13: 'Certificazione ex ante 5.0',
        14: 'Carta di identità dell\'ingegnere energetico',
        15: 'Libro cespiti',
        16: 'Visura catastale della struttura produttiva',
        17: 'Contratto di affitto della struttura produttiva',
        18: 'Documento sicurezza lavoro',
        19: 'Assicurazione professionale ingegnere 4.0',
        20: 'Fattura ingegnere 4.0',
        21: 'Assicurazione professionale ingegnere energetico',
        22: 'Fattura ingegnere energetico',
        23: 'Modulo di certificazione Esco/Ege',
        24: 'Dichiarazione di terzietà del valutatore indipendente',
        25: 'Carta di identità del firmatario della Certificazione contabile',
        26: 'Certificazione contabile',
        27: 'Fattura di acquisto del bene',
        28: 'Contratto di leasing'
    };

    constructor(private router: Router,
                private route: ActivatedRoute,
                private authService: AuthService,
                private modalService: NgbModal,
                private requestService: RequestService,
                private fileService: FileService,
                private documentService: DocumentService) {
    }

    ngOnInit(): void {
        this.authService.isUserSignedIn()
            .subscribe({
                next: () => {
                    this.authService.getUser()
                        .subscribe({
                            next: (user: any) => {
                                this.user = user;
                            },
                            error: () => {
                                this.router.navigate(['/login']);
                            }
                        });
                },
                error: () => {
                    this.router.navigate(['/login']);
                }
            });

        // La vista attiva è riflessa nell'URL (?vista=dns|iper) così sopravvive al refresh.
        this.route.queryParamMap.subscribe(() => {
            this.applyRequestedView();
        });

        this.route.paramMap.subscribe(params => {
            this.requestService.getByID(params.get('id'))
                .subscribe({
                    next: (request: Request) => {
                        this.request = request;
                        initializeDepreciationAssetsState(this.request);
                        this.documentTypes[6] = (this.request.FKIDRequestType !== 5) ? this.documentTypes[6] : 'Contratto di consulenza firmato digitalmente';
                        this.checkRequest4 = this.request.FKIDRequestType === 2 || this.request.FKIDRequestType === 4;
                        this.request.InvestmentType = (this.request.InvestmentType !== null)
                            ? this.request.InvestmentType?.replace(/,/g, ', ')!
                            : 'Nessuno';
                        if (this.request.InvestmentAmount !== null && this.request.InvestmentAmount !== undefined) {
                            let investmentAmountParts = this.request.InvestmentAmount!.toString().split('.');
                            let counter = 1;
                            while (counter <= investmentAmountParts[0].length) {
                                if (counter % 3 === 0 && counter !== investmentAmountParts[0].length) {
                                    this.investmentAmountParsed = '.' + investmentAmountParts[0][investmentAmountParts[0].length - counter] + this.investmentAmountParsed;
                                } else {
                                    this.investmentAmountParsed = investmentAmountParts[0][investmentAmountParts[0].length - counter] + this.investmentAmountParsed;
                                }
                                counter++;
                            }
                            if (investmentAmountParts[1] !== undefined) {
                                this.investmentAmountParsed += ',' + investmentAmountParts[1];
                            }
                        }
                        this.loadRequestDocuments();
                        this.applyRequestedView();
                    },
                    error: (err: HttpErrorResponse) => {
                        console.log(err);
                    }
                });
        });
    }

    /**
     * Tabella Documentazione: solo-pratica + satelliti di questa pratica.
     * I master cliente e i residui pre-migrazione dei tipi condivisi non compaiono.
     */
    private loadRequestDocuments(): void {
        if (!this.request.IDRequest) {
            this.documents = [];
            return;
        }

        this.documentService.getAllByIDRequest(this.request.IDRequest.toString())
            .subscribe({
                next: (documentsByType: any) => {
                    this.documents = [];
                    Object.keys(documentsByType || {}).forEach((typeId) => {
                        const bucket = documentsByType[typeId];
                        if (!Array.isArray(bucket)) {
                            return;
                        }
                        bucket.forEach((currentDocument: Document) => {
                            if (this.isVisibleOnRequestDetail(currentDocument)) {
                                this.documents.push(currentDocument);
                            }
                        });
                    });
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    private isVisibleOnRequestDetail(document: Document): boolean {
        if (isCustomerDocumentType(document.FKIDDocumentType)) {
            return isCustomerSatelliteDocument(document)
                && Number(document.FKIDRequest) === Number(this.request.IDRequest);
        }
        return true;
    }

    downloadDocument(document: Document) {
        this.requestDocumentFile(document)
            .subscribe({
                next: (blobFile) => {
                    saveAs(blobFile, document.Path);
                },
                error: (err: HttpErrorResponse) => {
                    console.log(err);
                }
            });
    }

    previewDocument(document: Document) {
        openDocumentPreview(this.modalService, document.Path, () => this.requestDocumentFile(document));
    }

    /**
     * Per i tipi master/satellite si passa anche l'IDCustomer: si legge la copia
     * storica della pratica e, se manca, il master del cliente.
     */
    private requestDocumentFile(document: Document) {
        const idCustomerParam = isCustomerDocumentType(document.FKIDDocumentType) && this.request.FKIDCustomer
            ? this.request.FKIDCustomer.toString()
            : null;

        return this.fileService.download(
            this.request.IDRequest!.toString(),
            document.FKIDDocumentType!.toString(),
            document.Path,
            idCustomerParam
        );
    }

    /** La Vista Domanda Nuova Sabatini esiste solo per DNS o DNS + Iperammortamento 4.0 in stato Completato. */
    get canShowDnsView(): boolean {
        return isDnsSabatiniViewRequest(this.request);
    }

    /** La Vista Iperammortamento 4.0 esiste solo per Iper 4.0 o DNS + Iper 4.0 in stato Completato. */
    get canShowIperView(): boolean {
        return isIperViewRequest(this.request);
    }

    setView(view: RequestDetailView): void {
        if (view === 'dns' && !this.canShowDnsView) {
            return;
        }
        if (view === 'iper' && !this.canShowIperView) {
            return;
        }
        this.activeView = view;
        this.router.navigate([], {
            relativeTo: this.route,
            queryParams: {vista: view === 'base' ? null : view},
            queryParamsHandling: 'merge',
            replaceUrl: true
        });
    }

    openDeleteRequestModal() {
        const modalRef = this.modalService.open(RequestDeleteModalComponent);
        modalRef.componentInstance.request = this.request;
        modalRef.result.then((requestBack: Request) => {
            if (requestBack) {
                this.requestService.delete(requestBack.IDRequest)
                    .subscribe({
                        next: () => {
                            this.router.navigate(['/dashboard']);
                        },
                        error: (err: HttpErrorResponse) => {
                            console.log(err);
                        }
                    });
            }
        }, () => {});
    }

    private applyRequestedView(): void {
        const vista = this.route.snapshot.queryParamMap.get('vista');
        if (vista === 'dns' && this.canShowDnsView) {
            this.activeView = 'dns';
            return;
        }
        if (vista === 'iper' && this.canShowIperView) {
            this.activeView = 'iper';
            return;
        }
        this.activeView = 'base';
        if ((vista === 'dns' || vista === 'iper') && this.request.IDRequest) {
            this.router.navigate([], {
                relativeTo: this.route,
                queryParams: {vista: null},
                queryParamsHandling: 'merge',
                replaceUrl: true
            });
        }
    }

    get isRequestType67(): boolean {
        return this.request.FKIDRequestType === 6 || this.request.FKIDRequestType === 7;
    }

    get showSingleDepreciationRate(): boolean {
        return this.isRequestType67
            && !hasMultipleDepreciationAssets(this.request.DepreciationAssets)
            && this.request.DepreciationRate !== null
            && this.request.DepreciationRate !== undefined;
    }

    get showMultipleDepreciationAssets(): boolean {
        return this.isRequestType67
            && hasMultipleDepreciationAssets(this.request.DepreciationAssets);
    }

    get parsedDepreciationAssets(): DepreciationAsset[] {
        return this.request.DepreciationAssets ?? [];
    }

    get hasPaymentDetails(): boolean {
        return !!this.request.PaymentDetails?.trim();
    }

    get showLeasingFields(): boolean {
        return !!this.request.InvestmentMode && this.request.InvestmentMode !== 'Fondi Propri';
    }

    get investmentDurationDisplay(): string {
        const duration = this.request.InvestmentDuration?.toString().trim();
        return duration ? `${duration} anni` : '-';
    }

    get operationalHeadquartersDisplay(): string {
        const city = this.request.OperationalHeadquarters?.trim() || '';
        const province = this.request.OperationalHeadquartersProvince?.trim() || '';
        const address = this.request.OperationalHeadquartersAddress?.trim() || '';
        const houseNumber = this.request.OperationalHeadquartersHouseNumber?.trim() || '';
        const location = city
            ? (province ? `${city} (${province})` : city)
            : '';
        const street = [address, houseNumber].filter(Boolean).join(' ');
        const parts = [location, street].filter(Boolean);
        return parts.length ? parts.join(' ') : '-';
    }

    get customerAddressDisplay(): string {
        const address = this.request.customer?.Address?.trim() || '';
        const houseNumber = this.request.customer?.HouseNumber?.trim() || '';
        const parts = [address, houseNumber].filter(Boolean);
        return parts.length ? parts.join(' ') : '-';
    }

    get investmentAmountDisplay(): string {
        return this.investmentAmountParsed ? `${this.investmentAmountParsed} euro` : '-';
    }

    displayValue(value: string | number | null | undefined): string {
        if (value === null || value === undefined) {
            return '-';
        }
        const trimmed = String(value).trim();
        return trimmed.length ? trimmed : '-';
    }

    referrerLabel(referrer: { Name?: string; Surname?: string; Email?: string }): string {
        const name = `${referrer.Name || ''} ${referrer.Surname || ''}`.trim();
        return name || referrer.Email || '-';
    }

    get signalParties(): Array<{ kind: 'referrer' | 'supplier'; label: string; kindLabel: string; link: any[] }> {
        const referrers: Array<{ kind: 'referrer' | 'supplier'; label: string; kindLabel: string; link: any[] }> = (this.request?.referrers || [])
            .filter(referrer => referrer.IDReferrer != null)
            .map(referrer => ({
                kind: 'referrer' as const,
                label: this.referrerLabel(referrer),
                kindLabel: 'Segnalatore',
                link: ['/segnalatori', referrer.IDReferrer]
            }));
        const suppliers = (this.request?.suppliers || [])
            .filter(supplier => supplier.IDSupplier != null)
            .map(supplier => ({
                kind: 'supplier' as const,
                label: (supplier.Name || '').trim() || (supplier.Email || '').trim() || (supplier.CF || '').trim() || '-',
                kindLabel: 'Fornitore',
                link: ['/fornitori', supplier.IDSupplier]
            }));
        return referrers.concat(suppliers);
    }

    displayBoolean(value: boolean | null | undefined): string {
        if (value === null || value === undefined) {
            return '-';
        }
        return value ? 'Sì' : 'No';
    }

    displayPlace(place?: string | null, province?: string | null): string {
        const normalizedPlace = place?.trim() || '';
        const normalizedProvince = province?.trim() || '';
        if (!normalizedPlace && !normalizedProvince) {
            return '-';
        }
        if (!normalizedPlace) {
            return `(${normalizedProvince})`;
        }
        return normalizedProvince
            ? `${normalizedPlace} (${normalizedProvince})`
            : normalizedPlace;
    }

}
